import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { AlertCircle, ChevronDown, ChevronRight, ScanSearch } from 'lucide-react';
import { parseDtcInput } from '../../../lib/dtcDecoder/parser';
import { decodeUdsDtcMessage } from '../../../lib/dtcDecoder/uds19';
import { hex2, bytesToHex } from '../../../lib/dtcDecoder/constants';
import { statusToBinary } from '../../../lib/dtcDecoder/statusByte';
import { ByteTrace, CopyButton, NoteList, SectionLabel, VERDICT_TONE, WarningList } from './shared';

const EXAMPLES = [
  ['59 02 FF 03 01 00 2F C1 00 00 08', '0x19 02 response'],
  ['59 01 FF 01 00 02', '0x19 01 count'],
  ['59 0A 09 0A 1F 00 09 0A 80 00 08 01 71 11 00', '0x19 0A supported'],
  ['59 04 0A 1F 00 29 01 01 F1 86 03', '0x19 04 snapshot'],
  ['59 02 FF 03 01 00 2F C1 00', 'truncated'],
  ['19 02 09', '0x19 02 request'],
  ['14 FF FF FF', '0x14 clear'],
];

function decodeText(text) {
  const r = parseDtcInput(text, { force: 'uds' });
  if (!r.ok) return { error: r.error };
  if (!r.bytes) return { error: 'That is a DTC code, not a UDS message — decode it in the DTC Code tab, or paste a response such as 59 02 FF 03 01 00 2F.' };
  if (![0x59, 0x19, 0x14, 0x54].includes(r.bytes[0])) {
    return { error: `This tab decodes UDS 0x19 / 0x14 messages, which start with 59, 19, 14 or 54. The input starts with 0x${hex2(r.bytes[0])} — try the smart input above.` };
  }
  const d = decodeUdsDtcMessage(r.bytes);
  if (!d.ok) return { error: d.error };
  return { ...d, canInfo: r.canInfo, isoTp: r.isoTp, parseWarnings: r.warnings || [], bytes: r.bytes };
}

const DtcRowDetail = ({ row }) => (
  <div className="grid md:grid-cols-2 gap-4 py-2 text-xs" data-testid={`dtc-uds-row-detail-${row.index}`}>
    <div className="space-y-1.5">
      <SectionLabel>DTC {row.code}</SectionLabel>
      <p>{row.info.entry ? row.info.entry.description : row.info.unknownReason}</p>
      {row.info.entry && <p className="text-muted-foreground"><span className="font-semibold text-foreground">Typical causes:</span> {row.info.entry.causes}</p>}
      <p className="text-muted-foreground">{row.info.letterInfo.name} · {row.info.digit1.label}{row.info.subgroup ? ` · ${row.info.subgroup}` : ''}</p>
      <p><span className="font-semibold">FTB 0x{row.ftb.hex}:</span> {row.ftb.category}{row.ftb.name ? ` — ${row.ftb.name}` : ''}</p>
    </div>
    <div className="space-y-1.5">
      <SectionLabel>Status 0x{hex2(row.status)} ({statusToBinary(row.status)})</SectionLabel>
      <p className={`rounded border px-2 py-1 ${VERDICT_TONE[row.verdict.primary.tone]}`}>{row.verdict.primary.title}</p>
      <ul className="space-y-0.5">
        {row.bits.map((b) => <li key={b.index}><span className="font-mono">bit {b.index} {b.name}</span></li>)}
        {row.bits.length === 0 && <li className="text-muted-foreground">No status bits set.</li>}
      </ul>
      {row.contradictions.map((c) => <p key={c.key} className="text-amber-700 dark:text-amber-400">Suspicious: {c.text}</p>)}
    </div>
  </div>
);

const UdsResponsePanel = ({ seed }) => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState({});

  const decode = (text) => { setOpen({}); setResult(decodeText(text)); };

  useEffect(() => {
    if (!seed) return;
    setInput(seed.text);
    decode(seed.text);
  }, [seed]);

  const buildCopy = () => {
    const lines = [result.title, `Bytes: ${bytesToHex(result.bytes)}`, result.summary];
    if (result.dtcs.length) {
      lines.push('');
      result.dtcs.forEach((d) => lines.push(`${d.code}-${d.ftb.hex}  status 0x${hex2(d.status)}  ${d.verdict.primary.title}${d.info.entry ? `  — ${d.info.entry.description}` : ''}`));
    }
    [...result.warnings].forEach((w) => lines.push(`Warning: ${w}`));
    return lines.join('\n');
  };

  return (
    <div className="space-y-6" data-testid="dtc-uds-panel">
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">UDS 0x19 / 0x14 Decoder</CardTitle>
          <CardDescription>Paste a ReadDTCInformation response (59 …) or request (19 …), or a ClearDiagnosticInformation message (14 … / 54). Sub-functions 01, 02, 04, 06 and 0A are fully decoded.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={(e) => { e.preventDefault(); decode(input); }} className="space-y-2">
            <Textarea className="font-mono min-h-[72px]" value={input} onChange={(e) => setInput(e.target.value)} placeholder="59 02 FF 03 01 00 2F C1 00 00 08" aria-label="UDS 0x19 message" data-testid="dtc-uds-input" />
            <Button type="submit" className="btn-premium" data-testid="dtc-uds-decode"><ScanSearch className="h-4 w-4" /> Decode</Button>
          </form>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground mr-1">Examples:</span>
            {EXAMPLES.map(([v, l]) => (
              <Badge key={v} variant="outline" className="font-mono text-[11px] font-normal cursor-pointer hover:bg-accent py-1" onClick={() => { setInput(v); decode(v); }} data-testid={`dtc-uds-example-${l.replace(/\s+/g, '-')}`}>{l}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {result?.error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="dtc-uds-error">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /><span>{result.error}</span>
        </div>
      )}

      {result && !result.error && (
        <Card className="technical-card" data-testid="dtc-uds-result">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1 min-w-0">
                <CardTitle className="font-chivo uppercase tracking-tight text-base break-words">{result.title}</CardTitle>
                <CardDescription data-testid="dtc-uds-summary">{result.summary}</CardDescription>
                {result.canInfo && <p className="text-xs font-mono text-muted-foreground" data-testid="dtc-uds-can">CAN ID {result.canInfo.text}</p>}
              </div>
              <CopyButton getText={buildCopy} testId="dtc-uds-copy" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <WarningList items={[...result.parseWarnings, ...result.warnings]} testId="dtc-uds-warnings" />
            <ByteTrace cells={result.cells} testId="dtc-uds-trace" />
            {result.dtcs.length > 0 && (
              <div className="rounded-md border border-structure overflow-hidden">
                <Table data-testid="dtc-uds-table">
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-6" />
                      <TableHead className="font-mono text-[11px] uppercase tracking-wider">DTC</TableHead>
                      <TableHead className="font-mono text-[11px] uppercase tracking-wider">FTB</TableHead>
                      <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                      <TableHead className="font-mono text-[11px] uppercase tracking-wider">Bits</TableHead>
                      <TableHead className="font-mono text-[11px] uppercase tracking-wider">Verdict</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.dtcs.map((d) => (
                      <React.Fragment key={d.index}>
                        <TableRow className="cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [d.index]: !o[d.index] }))} aria-expanded={!!open[d.index]} data-testid={`dtc-uds-row-${d.index}`}>
                          <TableCell className="px-2">{open[d.index] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                          <TableCell className="font-mono font-semibold whitespace-nowrap" data-testid={`dtc-uds-row-code-${d.index}`}>{d.code}</TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap">{d.ftb.hex}</TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap" data-testid={`dtc-uds-row-status-${d.index}`}>0x{hex2(d.status)}</TableCell>
                          <TableCell className="font-mono text-[10px] text-muted-foreground min-w-[120px]">{d.bits.map((b) => b.short).join(' ') || '—'}</TableCell>
                          <TableCell className="text-xs min-w-[140px]">{d.verdict.primary.title}{d.contradictions.length ? ' ⚠' : ''}</TableCell>
                        </TableRow>
                        {open[d.index] && (
                          <TableRow className="hover:bg-transparent bg-muted/20">
                            <TableCell colSpan={6}><DtcRowDetail row={d} /></TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <NoteList items={result.notes} testId="dtc-uds-notes" />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UdsResponsePanel;
